import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Previous/next pager.
 *
 * The visible "3 / 7" reads as ambiguous out loud, so it is hidden from
 * assistive tech and paired with a spelled-out equivalent.
 */
export function Pagination({
  page,
  totalPages,
  onPageChange,
  disabled = false,
  className,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Set while a page is in flight, to stop double-stepping. */
  disabled?: boolean;
  className?: string;
}) {
  if (totalPages <= 1) return null;

  return (
    <nav
      aria-label="Pagination"
      className={cn("flex items-center justify-center gap-2", className)}
    >
      <Button
        variant="outline"
        size="icon"
        disabled={page <= 1 || disabled}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
      <span className="text-sm text-muted-foreground tabular-nums">
        <span aria-hidden="true">
          {page} / {totalPages}
        </span>
        <span className="sr-only">
          Page {page} of {totalPages}
        </span>
      </span>
      <Button
        variant="outline"
        size="icon"
        disabled={page >= totalPages || disabled}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    </nav>
  );
}
